import React, { useRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import type { GetServerSideProps, GetServerSidePropsResult } from 'next';
import cn from 'classnames';


import { wrapper } from 'redux-store';

import { setRegion } from 'redux-store/slices/ui';

import type { I18nProps } from 'app.types';
import type { ProductsTypeV3 } from 'domains/products/products.types';

import { ProcessingPageAbstract } from 'containers/payments/ProcessingPageAbstract';
import { ProductsListBlackFriday } from 'containers/payments/ProductsListBlackFridayV1B';

import * as productSelectors from 'domains/products/products.selectors';
import { newPromoSpecialService } from 'domains/pages/promo-special-new';

import { ProductBlackFriday } from 'components/domains/payments/ProductBlackFridayV1B';
import { H2 } from 'components/domains/typography/Typography';
import Page from 'components/layouts/Page';


import { startSpanAndSetTransaction } from 'utils/APMService';
import getTranslate from 'utils/getTranslate';

import PaymentProcessingSuccess from 'components/domains/payments/PaymentProcessingBFSuccess';

import styles from './styles.module.scss';

const PremiumFeaturesPage: React.FC = () => {

  const router = useRouter();
  
  const titleQuery = router.query.title as string;
  const selectedIdQuery = router.query.selected as string;

  let products = useSelector(productSelectors.getProductsSelector);

  products = products.filter(product => !(/year|lifetime/.test(product.prices[0].interval) === false));

  products.sort((a, b) => {
    if (a.prices[0].interval === 'year' && b.prices[0].interval !== 'year') return -1;
    if (a.prices[0].interval !== 'year' && b.prices[0].interval === 'year') return 1;
    return 0;
  });

  const selectedProduct = products.find(product => product.id === +selectedIdQuery);
  const defaultProduct = selectedIdQuery && selectedProduct ? selectedProduct : products.find(product => product.is_paid === false);

  const [activeProduct, setActiveProduct] = useState(defaultProduct);
  
  const portalTargetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.backgroundColor = 'black';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  const onChangeActiveProduct = (product: ProductsTypeV3) => {
    setActiveProduct(product);
  }

  const oneProduct = products.length === 1;
  // One payment button for products. 2 buttons for one product.
  const onePaymentButton = !oneProduct;

  let title = `Your special offer on Welltory Premium`;
  if (titleQuery === 'step') {
    title = 'One step away from Premium';
  }

  const paymentNextButtonText = activeProduct?.prices[0].interval === 'lifetime' ? 'Get Lifetime Offer' : activeProduct?.prices[0].interval === 'year' ? 'Get Annual Offer' : 'pay with card';

  const extraStripeElementStyles = {
    default: {
      base: {
        fontFamily: 'November',
        fontSize: '20px',
        lineHeight: '32px',
        fontWeight: '400',
        color: 'rgba(43, 54, 71, 0.72)',
        '::placeholder': {
          color: '#A5A8AD',
        },
      },
      invalid: {
        color: '#ff003f',
      },
    },
    mobile: {
      base: {
        fontFamily: 'November',
        fontSize: '18px',
        lineHeight: '28px',
        fontWeight: '400',
        color: 'rgba(43, 54, 71, 0.72)',
        '::placeholder': {
          color: '#A5A8AD',
        },
      },
      invalid: {
        color: '#ff003f',
      },
    }
  };

  return (
    <Page
      withoutFooter
      fullWidth
      fullHeight
      hideLinkInHeader
      className={styles.page}
    >
      <ProcessingPageAbstract
       screen="blackfriday"
       className={styles.paymentButtonWrapper}
       nextButtonText={paymentNextButtonText}
       nextButtonClassName={cn(styles.nextButtonPayment)}
       paymentRequestButtonStyle="custom"
      //  nextButtonPortalTarget={portalTargetRef}
       onePaymentButton={onePaymentButton}
       withLoadingProcess={false}
       theme="dark"
       extraStripeElementStyles={extraStripeElementStyles}
       successPageComponent={PaymentProcessingSuccess}
      >
        <div className={styles.paymentPageContainer}>

          <div className={cn(styles.title)}>
            <H2 dangerouslySetInnerHTML={{ __html: title }}></H2>
          </div>
          <ProductsListBlackFriday
            component={ProductBlackFriday}
            products={products}
            activeProduct={activeProduct}
            portalTarget={portalTargetRef}
            onChange={onChangeActiveProduct}
          />
        </div>
      </ProcessingPageAbstract>
    </Page>
  );
};

export default PremiumFeaturesPage;

export const getServerSideProps: GetServerSideProps =
  wrapper.getServerSideProps(
    store =>
      async (context): Promise<GetServerSidePropsResult<I18nProps>> => {
        const endSpan = startSpanAndSetTransaction(context.resolvedUrl);

        const region = (context.req.headers['cf-ipcountry'] as string) || '';
        if (region) {
          await store.dispatch(setRegion(region));
        }

        await newPromoSpecialService.server({ context, store });

        endSpan();

        return {
          props: {
            ...(await getTranslate(context)),
          },
        };
      },
  );
